import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { FEEXPAY_CURRENCY, computeFeexPayAmount, resolveXofRateFor, type FeexPayConfig } from '@/lib/feexpay';
import MarkPaidModal from './components/MarkPaidModal';

// ─────────────────────────────────────────────────────────────
// ZIFEK — SuperAdmin · Mes abonnements
// Suivi de TOUS les paiements d'abonnement par marchand, avec un
// focus sur FeexPay (Mobile Money + carte bancaire) : statut,
// référence, montant (devise du compte + équivalent XOF), méthode et date.
// ─────────────────────────────────────────────────────────────

interface SubscriptionRow {
  id: number;
  user_id: number | null;
  plan: string | null;
  period: string | null;
  prix: number | null;
  monaie: string | null;
  status: string | null;
  payment_method: string | null;
  last_payment_id: string | null;
  next_billing_date: string | null;
  note: string | null;
  created_at: string | null;
}

interface MerchantInfo {
  id: number;
  name: string;
  nomcommerce: string;
  email: string;
}

const STATUS_META: Record<string, { label: string; className: string; icon: string }> = {
  paid: { label: 'Payé', className: 'bg-accent-100 text-accent-700', icon: 'ri-checkbox-circle-line' },
  pending: { label: 'En attente', className: 'bg-secondary-100 text-secondary-700', icon: 'ri-time-line' },
  failed: { label: 'Échoué', className: 'bg-red-50 text-red-600', icon: 'ri-close-circle-line' },
  expired: { label: 'Expiré', className: 'bg-background-100 text-foreground-500', icon: 'ri-forbid-2-line' },
  cancelled: { label: 'Annulé', className: 'bg-background-100 text-foreground-500', icon: 'ri-forbid-2-line' },
};

const PROVIDER_LABELS: Record<string, string> = {
  feexpay: 'FeexPay',
  stripe: 'Stripe',
  paypal: 'PayPal',
  cmi: 'CMI',
  wave: 'Wave',
  flutterwave: 'Flutterwave',
};

function statusMeta(status: string | null) {
  return STATUS_META[(status || '').toLowerCase()] || {
    label: status || '—',
    className: 'bg-background-100 text-foreground-600',
    icon: 'ri-information-line',
  };
}

/** Sous-moyen FeexPay déduit de la note (« Mobile Money » / « Carte bancaire »). */
function feexSubMethod(note: string | null): 'mobile' | 'card' | null {
  const n = (note || '').toLowerCase();
  if (n.includes('carte')) return 'card';
  if (n.includes('mobile money')) return 'mobile';
  return null;
}

function methodLabel(row: SubscriptionRow): { label: string; icon: string; tone: string } {
  const provider = (row.payment_method || '').toLowerCase();
  const base = PROVIDER_LABELS[provider] || row.payment_method || '—';
  if (provider === 'feexpay') {
    const sub = feexSubMethod(row.note);
    if (sub === 'card') return { label: `${base} · Carte`, icon: 'ri-bank-card-line', tone: 'bg-primary-100 text-primary-700' };
    if (sub === 'mobile') return { label: `${base} · Mobile Money`, icon: 'ri-smartphone-line', tone: 'bg-accent-100 text-accent-900' };
    return { label: base, icon: 'ri-smartphone-line', tone: 'bg-secondary-100 text-secondary-900' };
  }
  return { label: base, icon: 'ri-bank-card-line', tone: 'bg-background-100 text-foreground-600' };
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function SuperAdminSubscriptionsPage() {
  const [rows, setRows] = useState<SubscriptionRow[]>([]);
  const [merchants, setMerchants] = useState<Record<number, MerchantInfo>>({});
  const [xofRate, setXofRate] = useState<number>(1);
  /** Taux de la devise de référence du SuperAdmin (1 MAD → XOF). */
  const [madRate, setMadRate] = useState<number>(61);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [showMarkPaid, setShowMarkPaid] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [subsRes, feexRes] = await Promise.all([
        supabase
          .from('package_subscriptions')
          .select('id, user_id, plan, period, prix, monaie, status, payment_method, last_payment_id, next_billing_date, note, created_at')
          .order('created_at', { ascending: false })
          .limit(500),
        supabase.from('payment_providers').select('config').eq('provider', 'feexpay').maybeSingle(),
      ]);
      const list = (subsRes.data || []) as SubscriptionRow[];
      setRows(list);

      const cfg = (feexRes.data?.config || {}) as FeexPayConfig;
      const rate = Number(cfg.xof_rate);
      setXofRate(Number.isFinite(rate) && rate > 0 ? rate : 1);
      setMadRate(resolveXofRateFor('MAD', cfg));

      const userIds = [...new Set(list.map((r) => Number(r.user_id)).filter((n) => Number.isFinite(n) && n > 0))];
      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, name, nomcommerce, email')
          .in('id', userIds);
        const map: Record<number, MerchantInfo> = {};
        (users as MerchantInfo[] | null)?.forEach((u) => {
          map[Number(u.id)] = {
            id: Number(u.id),
            name: u.name || '',
            nomcommerce: u.nomcommerce || '',
            email: u.email || '',
          };
        });
        setMerchants(map);
      } else {
        setMerchants({});
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const merchantName = useCallback(
    (userId: number | null): string => {
      if (!userId) return 'Client Zifek';
      const m = merchants[Number(userId)];
      return m?.nomcommerce || m?.name || `Marchand #${userId}`;
    },
    [merchants],
  );

  const stats = useMemo(() => {
    const paid = rows.filter((r) => (r.status || '').toLowerCase() === 'paid');
    const pending = rows.filter((r) => (r.status || '').toLowerCase() === 'pending');
    const feex = rows.filter((r) => (r.payment_method || '').toLowerCase() === 'feexpay');
    // Total encaissé exprimé dans la devise de référence du SuperAdmin (MAD).
    const totalPaidMad = paid.reduce((s, r) => {
      const xof = computeFeexPayAmount(Number(r.prix) || 0, xofRate, r.monaie || 'MAD');
      return s + (madRate > 0 ? xof / madRate : xof);
    }, 0);
    const merchantCount = new Set(rows.map((r) => Number(r.user_id)).filter((n) => n > 0)).size;
    return { paidCount: paid.length, pendingCount: pending.length, feexCount: feex.length, totalPaidMad, merchantCount };
  }, [rows, xofRate, madRate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && (r.status || '').toLowerCase() !== statusFilter) return false;
      if (methodFilter !== 'all' && (r.payment_method || '').toLowerCase() !== methodFilter) return false;
      if (q) {
        const name = merchantName(r.user_id).toLowerCase();
        const email = (r.user_id ? merchants[Number(r.user_id)]?.email : '') || '';
        const ref = (r.last_payment_id || '').toLowerCase();
        if (!name.includes(q) && !email.toLowerCase().includes(q) && !ref.includes(q)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, methodFilter, query, merchantName, merchants]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* En-tête */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-bill-line text-xl text-background-50"></i>
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Mes abonnements</h1>
            <p className="text-sm text-foreground-500">Tous les paiements d&apos;abonnement, marchand par marchand.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setShowMarkPaid(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap w-fit"
          >
            <i className="ri-add-line"></i>
            Enregistrer un paiement
          </button>
          <button
            type="button"
            onClick={load}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap w-fit"
          >
            <i className="ri-refresh-line"></i>
            Actualiser
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="flex items-center gap-2 text-foreground-500 text-xs mb-1.5">
            <i className="ri-wallet-3-line text-accent-500"></i>Encaissé (payé)
          </div>
          <div className="text-lg md:text-xl font-bold font-heading text-foreground-950 tabular-nums">
            {stats.totalPaidMad.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            <span className="text-xs font-medium text-foreground-500 ml-1">MAD</span>
          </div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="flex items-center gap-2 text-foreground-500 text-xs mb-1.5">
            <i className="ri-checkbox-circle-line text-primary-500"></i>Abonnements payés
          </div>
          <div className="text-lg md:text-xl font-bold font-heading text-foreground-950 tabular-nums">{stats.paidCount}</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="flex items-center gap-2 text-foreground-500 text-xs mb-1.5">
            <i className="ri-time-line text-secondary-500"></i>En attente
          </div>
          <div className="text-lg md:text-xl font-bold font-heading text-foreground-950 tabular-nums">{stats.pendingCount}</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="flex items-center gap-2 text-foreground-500 text-xs mb-1.5">
            <i className="ri-smartphone-line text-accent-500"></i>Paiements FeexPay
          </div>
          <div className="text-lg md:text-xl font-bold font-heading text-foreground-950 tabular-nums">{stats.feexCount}</div>
          <div className="text-[11px] text-foreground-400 mt-0.5">{stats.merchantCount} marchand{stats.merchantCount > 1 ? 's' : ''}</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-5">
        <div className="flex flex-wrap items-center gap-1.5">
          {[
            { key: 'all', label: 'Tous les statuts' },
            { key: 'paid', label: 'Payés' },
            { key: 'pending', label: 'En attente' },
            { key: 'failed', label: 'Échoués' },
          ].map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setStatusFilter(s.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                statusFilter === s.key ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
              }`}
            >
              {s.label}
            </button>
          ))}
          <span className="w-px h-5 bg-background-200/70 mx-1 hidden sm:block"></span>
          {[
            { key: 'all', label: 'Tous moyens' },
            { key: 'feexpay', label: 'FeexPay' },
          ].map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMethodFilter(m.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                methodFilter === m.key ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="relative w-full lg:w-72">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un marchand, un email, une référence…"
            className="w-full h-11 pl-9 pr-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {/* Tableau */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-4">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-bill-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-500">Aucun abonnement ne correspond à ces filtres.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-foreground-400 border-b border-background-200/70">
                  <th className="py-3 px-5 font-medium">Date</th>
                  <th className="py-3 px-3 font-medium">Marchand</th>
                  <th className="py-3 px-3 font-medium">Formule</th>
                  <th className="py-3 px-3 font-medium">Moyen</th>
                  <th className="py-3 px-3 font-medium">Montant</th>
                  <th className="py-3 px-3 font-medium">Référence</th>
                  <th className="py-3 px-5 font-medium">Statut</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const meta = statusMeta(r.status);
                  const m = methodLabel(r);
                  const xof = computeFeexPayAmount(Number(r.prix) || 0, xofRate, r.monaie || 'MAD');
                  const merchant = r.user_id ? merchants[Number(r.user_id)] : null;
                  return (
                    <tr key={r.id} className="border-b border-background-200/50 last:border-0">
                      <td className="py-3 px-5 text-foreground-500 whitespace-nowrap">{formatDate(r.created_at)}</td>
                      <td className="py-3 px-3">
                        <div className="font-medium text-foreground-900 max-w-[200px] truncate">{merchantName(r.user_id)}</div>
                        {merchant?.email && (
                          <div className="text-[11px] text-foreground-400 max-w-[200px] truncate">{merchant.email}</div>
                        )}
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap">
                        <span className="text-foreground-800 font-medium">{r.plan || '—'}</span>
                        <span className="text-foreground-400 text-xs ml-1">· {r.period === 'yearly' ? 'annuel' : 'mensuel'}</span>
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${m.tone}`}>
                          <i className={m.icon}></i>{m.label}
                        </span>
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap">
                        <div className="font-semibold text-foreground-900">
                          {(Number(r.prix) || 0).toLocaleString('fr-FR')} {r.monaie || 'MAD'}
                        </div>
                        <div className="text-[11px] text-foreground-400">≈ {xof.toLocaleString('fr-FR')} {FEEXPAY_CURRENCY}</div>
                      </td>
                      <td className="py-3 px-3 text-foreground-600 max-w-[220px]">
                        <code className="text-xs break-all">{r.last_payment_id || '—'}</code>
                      </td>
                      <td className="py-3 px-5">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${meta.className}`}>
                          <i className={meta.icon}></i>{meta.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="mt-4 text-[11px] text-foreground-400 flex items-start gap-1.5 leading-relaxed">
        <i className="ri-information-line mt-0.5"></i>
        Le total « Encaissé » est converti dans la devise de référence du compte SuperAdmin (MAD). Chaque ligne conserve le montant réel de la formule et son équivalent {FEEXPAY_CURRENCY} utilisé pour le paiement FeexPay (Mobile Money / carte bancaire).
      </p>

      <MarkPaidModal
        open={showMarkPaid}
        onClose={() => setShowMarkPaid(false)}
        onSaved={() => { setShowMarkPaid(false); void load(); }}
      />
    </div>
  );
}